import React, { useState, useMemo } from 'react';
import {
  WarningIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  CaretDownIcon,
  FileXlsIcon,
  CircleNotchIcon,
  MagnifyingGlassIcon,
  SparkleIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import {
  AlertContainer as Alert,
  AlertDescription,
  AlertTitle,
} from 'src/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import {
  SingleSelect,
  SingleSelectOption,
} from 'src/components/common/SingleSelect';
import { Badge } from 'src/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { Card, CardContent, CardHeader } from 'src/components/ui/card';
import { Progress } from 'src/components/ui/progress';
import { Switch } from 'src/components/ui/switch';
import { Label } from 'src/components/ui/label';
import { Checkbox } from 'src/components/ui/checkbox';
import {
  useGetProductGroupsForMapping,
  useSuggestProductMappings,
  useImportHistoricalTransactions,
} from 'src/api/rpc/hooks/rebates/useHistoricalImport';
import { useRebateRulesByAgreement } from 'src/api/rebates/useRebateRules';
import type {
  GetAgreementImportContextResponse,
  ParseHistoricalCsvResponse,
  ProductCodeMapping,
  CsvProductCode,
} from '@erp/rpc';
import {
  NON_REBATEABLE_TURNOVER_TYPES,
  REBATEABLE_TURNOVER_TYPES,
} from '@erp/rpc';

// Helper type for candidate rule matching
interface CandidateRule {
  ruleId: string;
  ruleName: string;
  matchingTierRate: number;
  qualifyingProductGroups: Array<{ id: string; name: string }>;
  qualifyingInclusionType: string | null;
  // True when rule is "exclusive" with no product groups = "All Products"
  isAllProducts: boolean;
}

// Special value to represent non-rebateable products (maps to null productGroupId)
const NON_REBATEABLE_VALUE = '__NON_REBATEABLE__';

interface Step3ProductMappingProps {
  agreementContext: GetAgreementImportContextResponse;
  csvData: ParseHistoricalCsvResponse;
  year: number;
  onComplete: () => void;
  onBack: () => void;
  onError: (error: string) => void;
  isLoading: boolean;
  error?: string;
}

function Step3ProductMapping({
  agreementContext,
  csvData,
  year,
  onComplete,
  onBack,
  onError,
  isLoading,
  error,
}: Step3ProductMappingProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [mappings, setMappings] = useState<Record<string, string>>({});
  // Track which mappings came from AI suggestions
  const [aiSuggestedRefs, setAiSuggestedRefs] = useState<Set<string>>(
    new Set(),
  );
  // Toggle between inner join (strict - all rules) and outer join (any rule) for candidate groups
  const [useStrictMatching, setUseStrictMatching] = useState(true);

  // Import-related state (from Step4)
  const [saveMappings, setSaveMappings] = useState(true);
  const [confirmed, setConfirmed] = useState(true);

  const importMutation = useImportHistoricalTransactions();

  const { productGroups, isLoading: groupsLoading } =
    useGetProductGroupsForMapping(
      { supplierId: agreementContext.supplierId },
      true,
    );

  // Fetch rebate rules to match historical rates against tier percentages
  const { data: rebateRules, isLoading: rulesLoading } =
    useRebateRulesByAgreement(agreementContext.agreementId, true);

  const suggestMappingsMutation = useSuggestProductMappings();

  // Pre-compute lookup of all supplier product groups by ID
  const allProductGroupsById = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    if (productGroups) {
      for (const pg of productGroups) {
        map.set(pg.id, { id: pg.id, name: pg.name });
      }
    }
    return map;
  }, [productGroups]);

  // Tolerance for rate matching (0.01% = 0.01)
  const RATE_TOLERANCE = 0.01;

  // Pre-compute all tier rates from rebate rules and provide a fuzzy match function
  const matchesTierRate = useMemo(() => {
    const rates: number[] = [];
    if (rebateRules) {
      for (const rule of rebateRules) {
        for (const tier of rule.tiers) {
          const tierRate = parseFloat(tier.rebateValue);
          if (!isNaN(tierRate) && !rates.includes(tierRate)) {
            rates.push(tierRate);
          }
        }
      }
    }

    // Return function to check if a rate matches any tier rate within tolerance
    return (rate: number): boolean => {
      return rates.some(
        (tierRate) => Math.abs(rate - tierRate) <= RATE_TOLERANCE,
      );
    };
  }, [rebateRules]);

  // Find candidate rules based on historical rate matching (with 0.01% tolerance for rounding)
  const findCandidateRules = useMemo(() => {
    if (!rebateRules || rebateRules.length === 0) {
      return () => [];
    }

    // Tolerance for rate matching (0.01% = 0.01)
    const RATE_TOLERANCE = 0.01;

    // Pre-compute all rules with their tier rates for fuzzy matching
    const rulesWithRates: Array<{
      rule: CandidateRule;
      tierRate: number;
    }> = [];

    for (const rule of rebateRules) {
      // Check if this rule qualifies "All Products" (exclusive with no product groups)
      const isAllProducts =
        rule.qualifyingInclusionType === 'exclusive' &&
        rule.qualifyingProductGroups.length === 0;

      for (const tier of rule.tiers) {
        const tierRate = parseFloat(tier.rebateValue);
        if (!isNaN(tierRate)) {
          rulesWithRates.push({
            rule: {
              ruleId: rule.id,
              ruleName: rule.name,
              matchingTierRate: tierRate,
              qualifyingProductGroups: rule.qualifyingProductGroups,
              qualifyingInclusionType: rule.qualifyingInclusionType,
              isAllProducts,
            },
            tierRate,
          });
        }
      }
    }

    return (productCode: CsvProductCode): CandidateRule[] => {
      if (
        !productCode.historicalRates ||
        productCode.historicalRates.length === 0
      ) {
        return [];
      }

      const candidates: CandidateRule[] = [];
      const seenRuleIds = new Set<string>();

      for (const historicalRate of productCode.historicalRates) {
        // Find all rules with tier rates within tolerance
        for (const { rule, tierRate } of rulesWithRates) {
          if (
            Math.abs(historicalRate - tierRate) <= RATE_TOLERANCE &&
            !seenRuleIds.has(rule.ruleId)
          ) {
            seenRuleIds.add(rule.ruleId);
            candidates.push({ ...rule, matchingTierRate: tierRate });
          }
        }
      }

      return candidates;
    };
  }, [rebateRules]);

  // Build a lookup from existing product group mappings
  const existingMappings = useMemo(() => {
    const lookup: Record<string, string> = {};
    if (productGroups) {
      for (const group of productGroups) {
        for (const code of group.existingMappedCodes) {
          lookup[code] = group.id;
        }
      }
    }
    return lookup;
  }, [productGroups]);

  // Find the catch-all product group (for "All Products" rules)
  const catchAllGroup = useMemo(() => {
    return productGroups?.find((g) => g.isCatchAll);
  }, [productGroups]);

  // Available group IDs for mapping
  const availableGroupIds = useMemo(() => {
    return new Set(productGroups?.map((g) => g.id) || []);
  }, [productGroups]);

  // Options for SingleSelect dropdown
  const productGroupOptions: SingleSelectOption[] = useMemo(() => {
    const options: SingleSelectOption[] = [
      {
        id: NON_REBATEABLE_VALUE,
        label: 'Non-Rebateable',
      },
    ];
    if (productGroups) {
      for (const group of productGroups) {
        options.push({
          id: group.id,
          label: group.isCatchAll ? `${group.name} (Catch-All)` : group.name,
        });
      }
    }
    return options;
  }, [productGroups]);

  // Initialize mappings from existing codes
  const effectiveMappings = useMemo(() => {
    const result: Record<string, string> = { ...existingMappings };
    // Override with user selections
    Object.entries(mappings).forEach(([key, value]) => {
      result[key] = value;
    });
    return result;
  }, [existingMappings, mappings]);

  // Filter product codes by search term
  const filteredCodes = useMemo(() => {
    if (!searchTerm.trim()) {
      return csvData.productCodes;
    }
    const term = searchTerm.toLowerCase();
    return csvData.productCodes.filter(
      (code) =>
        code.productReference.toLowerCase().includes(term) ||
        code.product.toLowerCase().includes(term),
    );
  }, [csvData.productCodes, searchTerm]);

  const handleMappingChange = (productRef: string, groupId: string) => {
    setMappings((prev) => ({
      ...prev,
      [productRef]: groupId,
    }));
  };

  const handleBulkAssign = (groupId: string) => {
    // Assign unmapped rebateable codes to this group
    // (non-rebateable by turnover type are always null)
    const newMappings: Record<string, string> = { ...mappings };
    for (const code of rebateableProducts) {
      if (!effectiveMappings[code.productReference]) {
        newMappings[code.productReference] = groupId;
      }
    }
    setMappings(newMappings);
  };

  const handleGetAiSuggestions = () => {
    suggestMappingsMutation.mutate(
      {
        supplierId: agreementContext.supplierId,
        // Send all products with their turnover type
        // Backend will auto-map non-rebateable and use AI for rebateable
        productCodes: csvData.productCodes.map((code) => ({
          productReference: code.productReference,
          product: code.product,
          turnoverType: code.turnoverType,
        })),
      },
      {
        onSuccess: (data) => {
          const newMappings: Record<string, string> = { ...mappings };
          const newAiRefs = new Set<string>();

          for (const suggestion of data.suggestions) {
            // Apply AI suggestion (overwrites any existing value)
            // Note: non-rebateable items will also be in suggestions with null productGroupId
            if (suggestion.suggestedProductGroupId) {
              newMappings[suggestion.productReference] =
                suggestion.suggestedProductGroupId;
            } else {
              // null = non-rebateable
              newMappings[suggestion.productReference] = NON_REBATEABLE_VALUE;
            }
            newAiRefs.add(suggestion.productReference);
          }

          setMappings(newMappings);
          setAiSuggestedRefs(newAiRefs);
        },
      },
    );
  };

  // Build the final mappings array
  const buildFinalMappings = (): ProductCodeMapping[] => {
    const finalMappings: ProductCodeMapping[] = [];
    for (const code of csvData.productCodes) {
      const isNonRebateable = NON_REBATEABLE_TURNOVER_TYPES.includes(
        code.turnoverType,
      );
      // Check if user has explicitly set a mapping (overriding default)
      const userMapping = effectiveMappings[code.productReference];

      if (userMapping && userMapping !== NON_REBATEABLE_VALUE) {
        // User explicitly mapped to a product group
        finalMappings.push({
          productReference: code.productReference,
          productGroupId: userMapping,
        });
      } else if (isNonRebateable && !userMapping) {
        // Non-rebateable by turnover type with no user override -> default to null
        finalMappings.push({
          productReference: code.productReference,
          productGroupId: null,
        });
      } else {
        // Rebateable products use user/AI mappings or default to non-rebateable
        const groupId = userMapping || NON_REBATEABLE_VALUE;
        finalMappings.push({
          productReference: code.productReference,
          // Convert special non-rebateable value to null
          productGroupId: groupId === NON_REBATEABLE_VALUE ? null : groupId,
        });
      }
    }
    return finalMappings;
  };

  const handleImport = async () => {
    if (!confirmed) {
      onError('Please confirm that you want to proceed with the import');
      return;
    }

    if (!agreementContext.rexelCode) {
      onError('No rexel code available');
      return;
    }

    try {
      await importMutation.mutateAsync({
        agreementId: agreementContext.agreementId,
        rexelCode: agreementContext.rexelCode,
        year,
        productCodeMappings: buildFinalMappings(),
        saveMappingsToProductGroups: saveMappings,
      });
      // Don't call onComplete() here - let the user review the results first
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Import failed');
    }
  };

  // Calculate estimated total value
  const estimatedTotalValue = csvData.productCodes.reduce(
    (sum, code) => sum + code.totalValue,
    0,
  );

  // Count items mapped to actual product groups (from final mappings perspective)
  const finalMappingsPreview = buildFinalMappings();
  const mappedToGroupCountFinal = finalMappingsPreview.filter(
    (m) => m.productGroupId !== null,
  ).length;
  const nonRebateableCountFinal =
    csvData.productCodes.length - mappedToGroupCountFinal;

  // Count non-rebateable by turnover type (auto-mapped)
  const nonRebateableByTurnoverType = csvData.productCodes.filter((c) =>
    NON_REBATEABLE_TURNOVER_TYPES.includes(c.turnoverType),
  ).length;

  // Separate rebateable and non-rebateable products by turnover type
  const rebateableProducts = csvData.productCodes.filter((c) =>
    REBATEABLE_TURNOVER_TYPES.includes(c.turnoverType),
  );
  const nonRebateableProducts = csvData.productCodes.filter((c) =>
    NON_REBATEABLE_TURNOVER_TYPES.includes(c.turnoverType),
  );

  // Count items mapped to actual product groups (not non-rebateable)
  // Note: non-rebateable by turnover type are always mapped to null
  const mappedToGroupCount = rebateableProducts.filter((c) => {
    const mapping = effectiveMappings[c.productReference];
    return mapping && mapping !== NON_REBATEABLE_VALUE;
  }).length;
  const unmappedRebateableCount =
    rebateableProducts.length - mappedToGroupCount;
  const nonRebateableCount = nonRebateableProducts.length;

  // Compute volume allocation breakdown by product group
  const volumeAllocationBreakdown = useMemo(() => {
    // Create a map of product group id -> { name, productCount, totalVolume }
    const groupBreakdown = new Map<
      string,
      { id: string; name: string; productCount: number; totalVolume: number }
    >();

    // Initialize with all product groups (so we show groups with 0 allocation too)
    productGroups?.forEach((pg) => {
      groupBreakdown.set(pg.id, {
        id: pg.id,
        name: pg.isCatchAll ? `${pg.name} (Catch-All)` : pg.name,
        productCount: 0,
        totalVolume: 0,
      });
    });

    // Track unmapped rebateable and non-rebateable separately
    let unmappedRebateableVolume = 0;
    let unmappedRebateableCount = 0;
    let nonRebateableVolume = 0;
    let nonRebateableProductCount = 0;

    // Aggregate volumes based on current mappings
    for (const product of csvData.productCodes) {
      const isNonRebateable = NON_REBATEABLE_TURNOVER_TYPES.includes(
        product.turnoverType,
      );
      const mapping = effectiveMappings[product.productReference];

      if (isNonRebateable) {
        // Non-rebateable products go to their own category
        nonRebateableVolume += product.totalValue;
        nonRebateableProductCount++;
      } else if (mapping && mapping !== NON_REBATEABLE_VALUE) {
        // Mapped to a product group
        const existing = groupBreakdown.get(mapping);
        if (existing) {
          existing.productCount++;
          existing.totalVolume += product.totalValue;
        }
      } else {
        // Unmapped rebateable
        unmappedRebateableVolume += product.totalValue;
        unmappedRebateableCount++;
      }
    }

    // Convert to array and filter out groups with no allocations
    const allocatedGroups = Array.from(groupBreakdown.values()).filter(
      (g) => g.productCount > 0,
    );

    // Calculate total volume for percentages
    const totalVolume =
      allocatedGroups.reduce((sum, g) => sum + g.totalVolume, 0) +
      unmappedRebateableVolume +
      nonRebateableVolume;

    return {
      allocatedGroups: allocatedGroups.sort(
        (a, b) => b.totalVolume - a.totalVolume,
      ),
      unmappedRebateable: {
        productCount: unmappedRebateableCount,
        totalVolume: unmappedRebateableVolume,
      },
      nonRebateable: {
        productCount: nonRebateableProductCount,
        totalVolume: nonRebateableVolume,
      },
      totalVolume,
    };
  }, [csvData.productCodes, effectiveMappings, productGroups]);

  // Helper function to find matching Enable rule by volume (within $5 tolerance)
  const findMatchingEnableRule = useMemo(() => {
    const VOLUME_TOLERANCE = 5; // $5 tolerance

    return (
      allocatedVolume: number,
    ): { programLineId: string; totalTransactions: number } | null => {
      if (!csvData.enableRules || csvData.enableRules.length === 0) {
        return null;
      }

      const matchingRule = csvData.enableRules.find(
        (rule) =>
          Math.abs(rule.totalTransactions - allocatedVolume) <=
          VOLUME_TOLERANCE,
      );

      return matchingRule
        ? {
            programLineId: matchingRule.programLineId,
            totalTransactions: matchingRule.totalTransactions,
          }
        : null;
    };
  }, [csvData.enableRules]);

  if (groupsLoading || rulesLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {groupsLoading
              ? 'Loading product groups...'
              : 'Loading rebate rules...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full gap-6">
      <div>
        <h2 className="text-lg font-semibold mb-2">Map Product Codes</h2>
        <p className="text-muted-foreground">
          Map each CSV product code to a product group. Existing mappings from
          previous imports are pre-filled.
        </p>
      </div>

      {/* Summary */}
      <div className="flex flex-wrap gap-4">
        <Badge variant="secondary" className="text-sm">
          {mappedToGroupCount} Rebateable Mapped
        </Badge>
        {unmappedRebateableCount > 0 && (
          <Badge variant="outline" className="text-sm text-amber-600">
            {unmappedRebateableCount} Rebateable Unmapped
          </Badge>
        )}
        <Badge variant="outline" className="text-sm">
          {nonRebateableCount} Non-Rebateable (auto-mapped)
        </Badge>
        <Badge variant="outline" className="text-sm">
          {productGroups?.length || 0} Product Groups Available
        </Badge>
      </div>

      {/* AI Suggestions */}
      <div className="rounded-lg border p-4 bg-muted/50">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">AI-Powered Mapping</p>
            <p className="text-sm text-muted-foreground">
              Use AI to suggest product group mappings for rebateable products.
              Non-rebateable items are automatically mapped.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleGetAiSuggestions}
            disabled={
              suggestMappingsMutation.isPending ||
              rebateableProducts.length === 0
            }
          >
            {suggestMappingsMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                Getting suggestions...
              </>
            ) : (
              <>
                <SparkleIcon className="h-4 w-4 mr-2" />
                Get AI Suggestions ({rebateableProducts.length} products)
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Bulk Actions */}
      {unmappedRebateableCount > 0 && catchAllGroup && (
        <div className="rounded-lg border p-4 bg-muted/50">
          <p className="text-sm mb-2">
            {unmappedRebateableCount} rebateable product codes are unmapped. You
            can assign them all to the catch-all group:
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleBulkAssign(catchAllGroup.id)}
          >
            Assign all unmapped to "{catchAllGroup.name}"
          </Button>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <Alert variant="destructive">
          <WarningIcon className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Search and Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search product codes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Candidate Matching Mode Toggle */}
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2">
                <Switch
                  id="strict-matching"
                  checked={useStrictMatching}
                  onCheckedChange={setUseStrictMatching}
                />
                <Label
                  htmlFor="strict-matching"
                  className="text-sm cursor-pointer whitespace-nowrap"
                >
                  Strict matching
                </Label>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <p className="font-medium mb-1">Candidate Product Groups Mode</p>
              <p className="text-xs text-muted-foreground">
                <strong>Off (default):</strong> Show all product groups from any
                matching rule (union/outer join)
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                <strong>On:</strong> Show only product groups that appear in all
                matching rules (intersection/inner join)
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Mapping Table */}
      <div className="flex-1 min-h-0 rounded-lg border overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product Reference</TableHead>
              <TableHead>Product Name</TableHead>
              <TableHead>Turnover Type</TableHead>
              <TableHead>Historical Rates</TableHead>
              <TableHead>Candidate Rules</TableHead>
              <TableHead>Candidate Product Groups</TableHead>
              <TableHead className="text-right">Transactions</TableHead>
              <TableHead className="text-right">Value</TableHead>
              <TableHead className="w-64">Product Group</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCodes.map((code) => {
              const currentMapping = effectiveMappings[code.productReference];
              const isFromExisting =
                existingMappings[code.productReference] === currentMapping &&
                !aiSuggestedRefs.has(code.productReference);
              const isFromAi = aiSuggestedRefs.has(code.productReference);
              const isNonRebateable = NON_REBATEABLE_TURNOVER_TYPES.includes(
                code.turnoverType,
              );
              const candidateRules = findCandidateRules(code);

              return (
                <TableRow
                  key={code.productReference}
                  className={isNonRebateable ? 'bg-muted/30' : ''}
                >
                  <TableCell className="font-mono text-sm">
                    {code.productReference}
                  </TableCell>
                  <TableCell>{code.product}</TableCell>
                  <TableCell>
                    <Badge
                      variant={isNonRebateable ? 'secondary' : 'outline'}
                      className="text-xs whitespace-nowrap"
                    >
                      {code.turnoverType}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {code.historicalRates && code.historicalRates.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {code.historicalRates.map((rate) => {
                          const matchesTier = matchesTierRate(rate);
                          return (
                            <Tooltip key={rate}>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant="outline"
                                  className={`text-xs ${
                                    matchesTier
                                      ? 'border-green-400 bg-green-50 text-green-700'
                                      : 'border-red-400 bg-red-50 text-red-700'
                                  }`}
                                >
                                  {rate}%
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                {matchesTier
                                  ? 'Matches a tier in rebate rules'
                                  : 'No matching tier in rebate rules'}
                              </TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {candidateRules.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {candidateRules.map((candidate) => (
                          <Tooltip key={candidate.ruleId}>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="secondary"
                                className="text-xs cursor-help bg-blue-100 text-blue-800 hover:bg-blue-200"
                              >
                                {candidate.ruleName} (
                                {candidate.matchingTierRate}%)
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <div className="space-y-1">
                                <p className="font-medium">
                                  Matches tier at {candidate.matchingTierRate}%
                                </p>
                                {candidate.qualifyingProductGroups.length >
                                  0 && (
                                  <div>
                                    <p className="text-xs text-muted-foreground">
                                      Qualifying Product Groups:
                                    </p>
                                    <ul className="text-xs list-disc list-inside">
                                      {candidate.qualifyingProductGroups.map(
                                        (pg) => (
                                          <li key={pg.id}>{pg.name}</li>
                                        ),
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-xs">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      // Collect unique qualifying product groups from all candidate rules
                      const candidateProductGroupsMap = new Map<
                        string,
                        {
                          id: string;
                          name: string;
                          fromRules: string[];
                          isAllProducts?: boolean;
                        }
                      >();

                      // Track if any rule qualifies "All Products"
                      const allProductsRules: string[] = [];

                      // Count non-AllProducts rules for strict matching
                      const nonAllProductsRules = candidateRules.filter(
                        (r) => !r.isAllProducts,
                      );
                      const nonAllProductsRuleCount =
                        nonAllProductsRules.length;

                      for (const rule of candidateRules) {
                        if (rule.isAllProducts) {
                          // Rule qualifies "All Products" - add catch-all group as candidate
                          allProductsRules.push(rule.ruleName);
                        } else {
                          // Determine which product groups this rule qualifies based on inclusion type
                          // Inclusive: listed groups are included
                          // Exclusive: listed groups are EXCLUDED, all others are included
                          let qualifyingGroups: Array<{
                            id: string;
                            name: string;
                          }>;

                          if (rule.qualifyingInclusionType === 'exclusive') {
                            // Exclusive: all supplier groups EXCEPT the ones listed
                            const excludedIds = new Set(
                              rule.qualifyingProductGroups.map((pg) => pg.id),
                            );
                            qualifyingGroups = Array.from(
                              allProductGroupsById.values(),
                            ).filter((pg) => !excludedIds.has(pg.id));
                          } else {
                            // Inclusive (default): use the listed groups directly
                            qualifyingGroups = rule.qualifyingProductGroups;
                          }

                          for (const pg of qualifyingGroups) {
                            const existing = candidateProductGroupsMap.get(
                              pg.id,
                            );
                            if (existing) {
                              existing.fromRules.push(rule.ruleName);
                            } else {
                              candidateProductGroupsMap.set(pg.id, {
                                id: pg.id,
                                name: pg.name,
                                fromRules: [rule.ruleName],
                              });
                            }
                          }
                        }
                      }

                      // If any rule qualifies "All Products", add the catch-all group
                      if (allProductsRules.length > 0 && catchAllGroup) {
                        const existing = candidateProductGroupsMap.get(
                          catchAllGroup.id,
                        );
                        if (existing) {
                          existing.fromRules.push(...allProductsRules);
                          existing.isAllProducts = true;
                        } else {
                          candidateProductGroupsMap.set(catchAllGroup.id, {
                            id: catchAllGroup.id,
                            name: catchAllGroup.name,
                            fromRules: allProductsRules,
                            isAllProducts: true,
                          });
                        }
                      }

                      let candidateProductGroups = Array.from(
                        candidateProductGroupsMap.values(),
                      );

                      // Apply strict matching filter if enabled
                      // Strict matching = inner join: only show groups in ALL non-AllProducts rules
                      // "All Products" rules match everything, so they don't filter
                      if (useStrictMatching && nonAllProductsRuleCount > 1) {
                        candidateProductGroups = candidateProductGroups.filter(
                          (pg) => {
                            // Count how many non-AllProducts rules include this group
                            const ruleCountForGroup = pg.fromRules.filter(
                              (ruleName) =>
                                !allProductsRules.includes(ruleName),
                            ).length;
                            // Keep only if it appears in ALL non-AllProducts rules
                            return ruleCountForGroup >= nonAllProductsRuleCount;
                          },
                        );
                      }

                      // Show "All Products" indicator if any rule has that, even without catch-all group
                      if (
                        candidateProductGroups.length === 0 &&
                        allProductsRules.length > 0
                      ) {
                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className="text-xs border-purple-300 text-purple-700"
                              >
                                All Products
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <div className="space-y-1">
                                <p className="font-medium">All Products</p>
                                <p className="text-xs text-muted-foreground">
                                  Rule{allProductsRules.length > 1 ? 's' : ''}:{' '}
                                  {allProductsRules.join(', ')}
                                </p>
                                <p className="text-xs text-amber-600">
                                  No catch-all group defined for this supplier
                                </p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        );
                      }

                      if (candidateProductGroups.length === 0) {
                        return (
                          <span className="text-muted-foreground text-xs">
                            -
                          </span>
                        );
                      }

                      return (
                        <div className="flex flex-wrap gap-1">
                          {candidateProductGroups.map((pg) => {
                            const isAvailable = availableGroupIds.has(pg.id);
                            const isCurrentlySelected =
                              currentMapping === pg.id;

                            return (
                              <Tooltip key={pg.id}>
                                <TooltipTrigger asChild>
                                  <Badge
                                    variant={
                                      isCurrentlySelected
                                        ? 'default'
                                        : 'outline'
                                    }
                                    className={`text-xs ${
                                      isAvailable
                                        ? isCurrentlySelected
                                          ? 'bg-green-600 text-white cursor-default'
                                          : pg.isAllProducts
                                            ? 'border-purple-300 text-purple-700 cursor-pointer hover:bg-purple-50'
                                            : 'border-green-300 text-green-700 cursor-pointer hover:bg-green-50'
                                        : 'border-gray-200 text-gray-400 cursor-not-allowed'
                                    }`}
                                    onClick={
                                      isAvailable && !isCurrentlySelected
                                        ? () =>
                                            handleMappingChange(
                                              code.productReference,
                                              pg.id,
                                            )
                                        : undefined
                                    }
                                  >
                                    {pg.isAllProducts
                                      ? `${pg.name} (All Products)`
                                      : pg.name}
                                    {isCurrentlySelected && ' - selected'}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <div className="space-y-1">
                                    <p className="font-medium">{pg.name}</p>
                                    {pg.isAllProducts && (
                                      <p className="text-xs text-purple-600">
                                        Catch-all for "All Products" rules
                                      </p>
                                    )}
                                    <p className="text-xs text-muted-foreground">
                                      From rule
                                      {pg.fromRules.length > 1 ? 's' : ''}:{' '}
                                      {pg.fromRules.join(', ')}
                                    </p>
                                    {isAvailable ? (
                                      isCurrentlySelected ? (
                                        <p className="text-xs text-green-600">
                                          Currently selected
                                        </p>
                                      ) : (
                                        <p className="text-xs text-blue-600">
                                          Click to apply this mapping
                                        </p>
                                      )
                                    ) : (
                                      <p className="text-xs text-amber-600">
                                        Not available for this supplier
                                      </p>
                                    )}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-right">
                    {code.transactionCount.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    $
                    {code.totalValue.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <SingleSelect
                        options={productGroupOptions}
                        value={
                          productGroupOptions.find(
                            (o) =>
                              o.id === (currentMapping || NON_REBATEABLE_VALUE),
                          ) || null
                        }
                        onChange={(selected) =>
                          handleMappingChange(
                            code.productReference,
                            selected?.id || NON_REBATEABLE_VALUE,
                          )
                        }
                        placeholder="Select product group..."
                        searchPlaceholder="Search groups..."
                        fullWidth
                        size="sm"
                        shouldAutoSort={false}
                      />
                      {isFromExisting && currentMapping && (
                        <Badge variant="outline" className="shrink-0 text-xs">
                          Existing
                        </Badge>
                      )}
                      {isFromAi && (
                        <Badge variant="secondary" className="shrink-0 text-xs">
                          AI
                        </Badge>
                      )}
                      {isNonRebateable && !isFromAi && !currentMapping && (
                        <Badge variant="secondary" className="shrink-0 text-xs">
                          Auto
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Volume Allocation Summary */}
      <Card>
        <Collapsible defaultOpen>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h3 className="text-sm font-semibold">
                    Volume Allocation Summary
                  </h3>
                  <Badge variant="outline" className="text-xs">
                    $
                    {volumeAllocationBreakdown.totalVolume.toLocaleString(
                      undefined,
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}{' '}
                    total
                  </Badge>
                </div>
                <CaretDownIcon className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180" />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product Group</TableHead>
                      <TableHead className="text-right"># Products</TableHead>
                      <TableHead className="text-right">
                        Allocated Volume
                      </TableHead>
                      <TableHead className="text-right">% of Total</TableHead>
                      <TableHead className="w-32">Distribution</TableHead>
                      <TableHead>Matched Enable Rule</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Allocated product groups */}
                    {volumeAllocationBreakdown.allocatedGroups.map((group) => {
                      const percentage =
                        volumeAllocationBreakdown.totalVolume > 0
                          ? (group.totalVolume /
                              volumeAllocationBreakdown.totalVolume) *
                            100
                          : 0;
                      const matchingRule = findMatchingEnableRule(
                        group.totalVolume,
                      );
                      const hasMatch = matchingRule !== null;

                      return (
                        <TableRow
                          key={group.id}
                          className={hasMatch ? 'bg-green-50' : ''}
                        >
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={
                                hasMatch
                                  ? 'border-green-500 bg-green-100 text-green-800'
                                  : 'border-green-300 text-green-700'
                              }
                            >
                              {group.name}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {group.productCount}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            $
                            {group.totalVolume.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </TableCell>
                          <TableCell className="text-right">
                            {percentage.toFixed(1)}%
                          </TableCell>
                          <TableCell>
                            <Progress value={percentage} className="h-2" />
                          </TableCell>
                          <TableCell>
                            {hasMatch ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge
                                    variant="default"
                                    className="bg-green-600 hover:bg-green-700 text-white font-mono text-xs"
                                  >
                                    {matchingRule.programLineId}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <div className="space-y-1">
                                    <p className="font-medium">
                                      Matched Enable Rule
                                    </p>
                                    <p className="text-xs">
                                      Enable Total: $
                                      {matchingRule.totalTransactions.toLocaleString(
                                        undefined,
                                        {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        },
                                      )}
                                    </p>
                                    <p className="text-xs">
                                      Difference: $
                                      {Math.abs(
                                        matchingRule.totalTransactions -
                                          group.totalVolume,
                                      ).toFixed(2)}
                                    </p>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <span className="text-muted-foreground text-xs">
                                -
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Unmapped rebateable */}
                    {volumeAllocationBreakdown.unmappedRebateable.productCount >
                      0 && (
                      <TableRow className="bg-amber-50/50">
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="border-amber-300 text-amber-700"
                          >
                            Unmapped (Rebateable)
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium text-amber-700">
                          {
                            volumeAllocationBreakdown.unmappedRebateable
                              .productCount
                          }
                        </TableCell>
                        <TableCell className="text-right font-medium text-amber-700">
                          $
                          {volumeAllocationBreakdown.unmappedRebateable.totalVolume.toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                        </TableCell>
                        <TableCell className="text-right text-amber-700">
                          {volumeAllocationBreakdown.totalVolume > 0
                            ? (
                                (volumeAllocationBreakdown.unmappedRebateable
                                  .totalVolume /
                                  volumeAllocationBreakdown.totalVolume) *
                                100
                              ).toFixed(1)
                            : 0}
                          %
                        </TableCell>
                        <TableCell>
                          <Progress
                            value={
                              volumeAllocationBreakdown.totalVolume > 0
                                ? (volumeAllocationBreakdown.unmappedRebateable
                                    .totalVolume /
                                    volumeAllocationBreakdown.totalVolume) *
                                  100
                                : 0
                            }
                            className="h-2 [&>div]:bg-amber-500"
                          />
                        </TableCell>
                        <TableCell>
                          <span className="text-muted-foreground text-xs">
                            -
                          </span>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Non-rebateable */}
                    {volumeAllocationBreakdown.nonRebateable.productCount >
                      0 && (
                      <TableRow className="bg-muted/30">
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className="text-muted-foreground"
                          >
                            Non-Rebateable
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium text-muted-foreground">
                          {volumeAllocationBreakdown.nonRebateable.productCount}
                        </TableCell>
                        <TableCell className="text-right font-medium text-muted-foreground">
                          $
                          {volumeAllocationBreakdown.nonRebateable.totalVolume.toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {volumeAllocationBreakdown.totalVolume > 0
                            ? (
                                (volumeAllocationBreakdown.nonRebateable
                                  .totalVolume /
                                  volumeAllocationBreakdown.totalVolume) *
                                100
                              ).toFixed(1)
                            : 0}
                          %
                        </TableCell>
                        <TableCell>
                          <Progress
                            value={
                              volumeAllocationBreakdown.totalVolume > 0
                                ? (volumeAllocationBreakdown.nonRebateable
                                    .totalVolume /
                                    volumeAllocationBreakdown.totalVolume) *
                                  100
                                : 0
                            }
                            className="h-2 [&>div]:bg-gray-400"
                          />
                        </TableCell>
                        <TableCell>
                          <span className="text-muted-foreground text-xs">
                            -
                          </span>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Total row */}
                    <TableRow className="bg-muted font-semibold">
                      <TableCell>
                        <span className="font-semibold">Total</span>
                      </TableCell>
                      <TableCell className="text-right">
                        {csvData.productCodes.length}
                      </TableCell>
                      <TableCell className="text-right">
                        $
                        {volumeAllocationBreakdown.totalVolume.toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}
                      </TableCell>
                      <TableCell className="text-right">100%</TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Warning if unmapped rebateable products exist */}
              {volumeAllocationBreakdown.unmappedRebateable.productCount >
                0 && (
                <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="flex items-start gap-2">
                    <WarningIcon className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800">
                        {
                          volumeAllocationBreakdown.unmappedRebateable
                            .productCount
                        }{' '}
                        rebateable product
                        {volumeAllocationBreakdown.unmappedRebateable
                          .productCount !== 1
                          ? 's'
                          : ''}{' '}
                        unmapped ($
                        {volumeAllocationBreakdown.unmappedRebateable.totalVolume.toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}
                        )
                      </p>
                      <p className="text-amber-700 mt-1">
                        These products will be treated as non-rebateable. Map
                        them to product groups above to include them in rebate
                        calculations.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Product Reference Detail Table */}
              <div className="mt-6">
                <h4 className="text-sm font-semibold mb-3">
                  Volume by Product Reference ({csvData.productCodes.length}{' '}
                  products)
                </h4>
                <div className="rounded-lg border overflow-hidden max-h-64 overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background">
                      <TableRow>
                        <TableHead>Product Reference</TableHead>
                        <TableHead>Product Name</TableHead>
                        <TableHead>Turnover Type</TableHead>
                        <TableHead className="text-right">Volume</TableHead>
                        <TableHead className="text-right">% of Total</TableHead>
                        <TableHead>Mapped To</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {csvData.productCodes
                        .slice()
                        .sort((a, b) => b.totalValue - a.totalValue)
                        .map((product) => {
                          const mapping =
                            effectiveMappings[product.productReference];
                          const isNonRebateable =
                            NON_REBATEABLE_TURNOVER_TYPES.includes(
                              product.turnoverType,
                            );
                          const mappedGroup = mapping
                            ? productGroups?.find((g) => g.id === mapping)
                            : null;
                          const percentage =
                            volumeAllocationBreakdown.totalVolume > 0
                              ? (product.totalValue /
                                  volumeAllocationBreakdown.totalVolume) *
                                100
                              : 0;

                          return (
                            <TableRow
                              key={product.productReference}
                              className={isNonRebateable ? 'bg-muted/30' : ''}
                            >
                              <TableCell className="font-mono text-xs">
                                {product.productReference}
                              </TableCell>
                              <TableCell className="text-xs max-w-40 truncate">
                                {product.product}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={
                                    isNonRebateable ? 'secondary' : 'outline'
                                  }
                                  className="text-xs"
                                >
                                  {isNonRebateable ? 'Non-Reb' : 'Rebateable'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right font-medium text-xs">
                                $
                                {product.totalValue.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </TableCell>
                              <TableCell className="text-right text-xs text-muted-foreground">
                                {percentage.toFixed(2)}%
                              </TableCell>
                              <TableCell>
                                {isNonRebateable ? (
                                  <Badge
                                    variant="secondary"
                                    className="text-xs text-muted-foreground"
                                  >
                                    Non-Rebateable
                                  </Badge>
                                ) : mappedGroup ? (
                                  <Badge
                                    variant="outline"
                                    className="text-xs border-green-300 text-green-700"
                                  >
                                    {mappedGroup.name}
                                    {mappedGroup.isCatchAll && ' (Catch-All)'}
                                  </Badge>
                                ) : (
                                  <Badge
                                    variant="outline"
                                    className="text-xs border-amber-300 text-amber-700"
                                  >
                                    Unmapped
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Import Summary */}
      <Card>
        <Collapsible defaultOpen>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileXlsIcon className="h-5 w-5 text-primary" />
                  <h3 className="text-sm font-semibold">Import Summary</h3>
                </div>
                <CaretDownIcon className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180" />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Agreement:</span>
                  <p className="font-medium">
                    {agreementContext.agreementName}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Supplier:</span>
                  <p className="font-medium">{agreementContext.supplierName}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Rexel Code:</span>
                  <p className="font-medium">{agreementContext.rexelCode}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Year:</span>
                  <p className="font-medium">{year}</p>
                </div>
              </div>

              <hr />

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">
                    Transactions to Import:
                  </span>
                  <p className="text-2xl font-bold text-green-600">
                    {csvData.filteredRows.toLocaleString()}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    Estimated Total Value:
                  </span>
                  <p className="text-2xl font-bold">
                    $
                    {estimatedTotalValue.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    Product Codes Mapped:
                  </span>
                  <p className="font-medium">
                    {mappedToGroupCountFinal} / {csvData.productCodes.length}
                    {nonRebateableCountFinal > 0 && (
                      <span className="text-muted-foreground ml-2">
                        ({nonRebateableCountFinal} non-rebateable)
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Date Ranges:</span>
                  {(() => {
                    const transactionsRange = `${csvData.dateRange.earliest} - ${csvData.dateRange.latest}`;
                    const earningsRange = `${csvData.earningsDateRange.earliest} - ${csvData.earningsDateRange.latest}`;
                    const rangesMismatch =
                      csvData.dateRange.earliest !==
                        csvData.earningsDateRange.earliest ||
                      csvData.dateRange.latest !==
                        csvData.earningsDateRange.latest;

                    return (
                      <div className="mt-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground w-24">
                            Transactions:
                          </span>
                          <span
                            className={`text-xs font-medium ${rangesMismatch ? 'text-red-600' : ''}`}
                          >
                            {transactionsRange}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground w-24">
                            Daily Earnings:
                          </span>
                          <span
                            className={`text-xs font-medium ${rangesMismatch ? 'text-red-600' : ''}`}
                          >
                            {earningsRange}
                          </span>
                        </div>
                        {rangesMismatch && (
                          <p className="text-xs text-red-600 mt-1">
                            Date ranges do not match
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Turnover Type Breakdown */}
              {csvData.turnoverTypeBreakdown &&
                csvData.turnoverTypeBreakdown.length > 0 && (
                  <div>
                    <span className="text-sm text-muted-foreground">
                      Turnover Type Breakdown:
                    </span>
                    <div className="mt-2 flex flex-wrap gap-3">
                      {csvData.turnoverTypeBreakdown.map((breakdown) => {
                        const isNonRebateable =
                          NON_REBATEABLE_TURNOVER_TYPES.includes(
                            breakdown.turnoverType,
                          );
                        return (
                          <div
                            key={breakdown.turnoverType}
                            className={`rounded-lg border px-3 py-2 ${
                              isNonRebateable ? 'bg-muted/50' : ''
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium">
                                {breakdown.turnoverType}
                              </span>
                              <Badge
                                variant={
                                  isNonRebateable ? 'secondary' : 'default'
                                }
                                className="text-xs"
                              >
                                {isNonRebateable
                                  ? 'Non-Rebateable'
                                  : 'Rebateable'}
                              </Badge>
                            </div>
                            <p className="text-sm">
                              {breakdown.rowCount.toLocaleString()} rows | $
                              {breakdown.totalValue.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              {/* What will happen */}
              <div className="rounded-lg border p-4 bg-muted/50">
                <h4 className="font-medium mb-3 text-sm">What will happen:</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <WarningIcon className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                    <span>
                      Remove existing invoice associations for this agreement
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                    <span>Create a single invoice for all transactions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                    <span>
                      Create rebate line items with product group assignments
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                    <span>Reset forecast to recalculate with new data</span>
                  </li>
                  {saveMappings && (
                    <li className="flex items-start gap-2">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      <span>Save product code mappings for future imports</span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Info for non-rebateable codes */}
              {nonRebateableCountFinal > 0 && (
                <Alert>
                  <WarningIcon className="h-4 w-4" />
                  <AlertTitle>Non-Rebateable Product Codes</AlertTitle>
                  <AlertDescription>
                    {nonRebateableCountFinal} product codes will be imported
                    without a product group assignment.
                    {nonRebateableByTurnoverType > 0 && (
                      <span className="block mt-1">
                        {nonRebateableByTurnoverType} are automatically
                        classified as non-rebateable based on their turnover
                        type.
                      </span>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {/* Options */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="saveMappings"
                    checked={saveMappings}
                    onCheckedChange={(checked) =>
                      setSaveMappings(checked === true)
                    }
                  />
                  <Label htmlFor="saveMappings" className="text-sm">
                    Save product code mappings to product groups (recommended)
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="confirm"
                    checked={confirmed}
                    onCheckedChange={(checked) =>
                      setConfirmed(checked === true)
                    }
                  />
                  <Label htmlFor="confirm" className="text-sm font-medium">
                    I confirm that I want to import{' '}
                    {csvData.filteredRows.toLocaleString()} transactions
                  </Label>
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Error Display for Import */}
      {importMutation.error && (
        <Alert variant="destructive">
          <WarningIcon className="h-4 w-4" />
          <AlertTitle>Import Error</AlertTitle>
          <AlertDescription>
            {importMutation.error instanceof Error
              ? importMutation.error.message
              : 'Import failed'}
          </AlertDescription>
        </Alert>
      )}

      {/* Success Display */}
      {importMutation.isSuccess && (
        <>
          <Alert className="border-green-500 bg-green-50">
            <CheckCircleIcon className="h-4 w-4 text-green-500" />
            <AlertTitle className="text-green-700">Import Complete</AlertTitle>
            <AlertDescription className="text-green-600">
              {importMutation.data.invoicesDeleted > 0 && (
                <span>
                  Cleaned up {importMutation.data.invoicesDeleted} existing
                  invoices and {importMutation.data.lineItemsDeleted} line
                  items.{' '}
                </span>
              )}
              Successfully created {importMutation.data.invoicesCreated} invoice
              with {importMutation.data.lineItemsCreated} line items. Total
              value: $
              {importMutation.data.totalValue.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </AlertDescription>
          </Alert>

          {/* Forecast vs Actual Earnings Comparison */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-semibold">
                Forecast vs Actual Earnings Comparison
              </h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <span className="text-sm text-muted-foreground">
                    Forecast Estimate (Calculated):
                  </span>
                  <p className="text-2xl font-bold text-blue-600">
                    {importMutation.data.forecastTotal !== null
                      ? `$${importMutation.data.forecastTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : 'Calculating...'}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">
                    Actual Earnings (Enable):
                  </span>
                  <p className="text-2xl font-bold text-green-600">
                    $
                    {importMutation.data.actualEarningsSum.toLocaleString(
                      undefined,
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                    )}
                  </p>
                </div>
              </div>
              {importMutation.data.forecastTotal !== null &&
                importMutation.data.actualEarningsSum > 0 && (
                  <div className="pt-2 border-t">
                    <span className="text-sm text-muted-foreground">
                      Variance:
                    </span>
                    {(() => {
                      const variance =
                        importMutation.data.forecastTotal -
                        importMutation.data.actualEarningsSum;
                      const variancePercent =
                        (variance / importMutation.data.actualEarningsSum) *
                        100;
                      const isPositive = variance >= 0;
                      return (
                        <p
                          className={`text-lg font-semibold ${isPositive ? 'text-blue-600' : 'text-amber-600'}`}
                        >
                          {isPositive ? '+' : ''}$
                          {variance.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{' '}
                          <span className="text-sm font-normal">
                            ({isPositive ? '+' : ''}
                            {variancePercent.toFixed(1)}%)
                          </span>
                        </p>
                      );
                    })()}
                  </div>
                )}
            </CardContent>
          </Card>

          {/* Per-Rule Breakdown */}
          {importMutation.data.ruleBreakdown &&
            importMutation.data.ruleBreakdown.length > 0 && (
              <Card>
                <CardHeader className="py-3">
                  <h3 className="text-sm font-semibold">
                    Forecast Breakdown by Rule
                  </h3>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Rule</TableHead>
                          <TableHead className="text-right">
                            Discount Rate
                          </TableHead>
                          <TableHead className="text-right">Volume</TableHead>
                          <TableHead className="text-right">Rebate</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importMutation.data.ruleBreakdown
                          .slice()
                          .sort((a, b) => b.totalRebate - a.totalRebate)
                          .map((rule) => (
                            <TableRow key={rule.ruleId}>
                              <TableCell className="font-medium">
                                {rule.ruleName}
                              </TableCell>
                              <TableCell className="text-right">
                                {rule.discountPercentage}%
                              </TableCell>
                              <TableCell className="text-right">
                                $
                                {rule.totalVolume.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </TableCell>
                              <TableCell className="text-right text-green-700 font-medium">
                                $
                                {rule.totalRebate.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </TableCell>
                            </TableRow>
                          ))}
                        {/* Total row */}
                        <TableRow className="bg-muted font-semibold">
                          <TableCell>Total</TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-right">
                            $
                            {importMutation.data.ruleBreakdown
                              .reduce((sum, r) => sum + r.totalVolume, 0)
                              .toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                          </TableCell>
                          <TableCell className="text-right text-green-700">
                            $
                            {importMutation.data.ruleBreakdown
                              .reduce((sum, r) => sum + r.totalRebate, 0)
                              .toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
        </>
      )}

      {/* Enable Rules Summary (from Daily Earnings CSV) */}
      {csvData.enableRules && csvData.enableRules.length > 0 && (
        <Card>
          <Collapsible defaultOpen>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="text-sm font-semibold">
                      Enable Rules Summary (from Daily Earnings)
                    </h3>
                    <Badge variant="outline" className="text-xs">
                      {csvData.enableRules.length} rules
                    </Badge>
                  </div>
                  <CaretDownIcon className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180" />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <div className="rounded-lg border overflow-hidden max-h-96 overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background">
                      <TableRow>
                        <TableHead>Program Line ID</TableHead>
                        <TableHead>Rebate Rates</TableHead>
                        <TableHead className="text-right">Daily Rows</TableHead>
                        <TableHead className="text-right">Volume</TableHead>
                        <TableHead className="text-right">Rebate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {csvData.enableRules
                        .slice()
                        .sort(
                          (a, b) =>
                            b.totalActualEarnings - a.totalActualEarnings,
                        )
                        .map((rule) => (
                          <TableRow key={rule.programLineId}>
                            <TableCell className="font-mono text-sm">
                              {rule.programLineId}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {rule.uniqueRates.map((rate) => (
                                  <Badge
                                    key={rate}
                                    variant="outline"
                                    className="text-xs"
                                  >
                                    {rate}%
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {rule.rowCount.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              $
                              {rule.totalTransactions.toLocaleString(
                                undefined,
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                },
                              )}
                            </TableCell>
                            <TableCell className="text-right font-medium text-green-700">
                              $
                              {rule.totalActualEarnings.toLocaleString(
                                undefined,
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                },
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      {/* Total row */}
                      <TableRow className="bg-muted font-semibold">
                        <TableCell>
                          <span className="font-semibold">Total</span>
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-right">
                          {csvData.enableRules
                            .reduce((sum, r) => sum + r.rowCount, 0)
                            .toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          $
                          {csvData.enableRules
                            .reduce((sum, r) => sum + r.totalTransactions, 0)
                            .toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                        </TableCell>
                        <TableCell className="text-right text-green-700">
                          $
                          {csvData.enableRules
                            .reduce((sum, r) => sum + r.totalActualEarnings, 0)
                            .toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Data extracted from Daily Earnings CSV. Program Line ID
                  identifies Enable rules. Total Transacted Value shows the
                  volume associated with each rule.
                </p>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={importMutation.isPending}
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex gap-2">
          {importMutation.isSuccess && (
            <Button
              variant="outline"
              onClick={handleImport}
              disabled={importMutation.isPending}
            >
              {importMutation.isPending ? (
                <>
                  <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                  Re-importing...
                </>
              ) : (
                'Re-import'
              )}
            </Button>
          )}
          {!importMutation.isSuccess ? (
            <Button
              onClick={handleImport}
              disabled={!confirmed || importMutation.isPending || isLoading}
            >
              {importMutation.isPending ? (
                <>
                  <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                'Start Import'
              )}
            </Button>
          ) : (
            <Button onClick={onComplete}>Go to Agreement</Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Step3ProductMapping;
